import type { Library } from './library';
import { testToneUrl } from './testTones';
import type { Playlist, Track } from './types';

export type RepeatMode = 'one' | 'all' | 'none';

/**
 * 播放器：管理当前播单、曲目队列与 <audio> 元素。
 * 事件：
 *   track   切换曲目（detail: { track, playlist, index }）
 *   state   播放/暂停/缓冲等状态变化
 *   error   音频加载失败
 */
export class Player extends EventTarget {
  readonly audio: HTMLAudioElement;
  playlist: Playlist | undefined;
  index = -1;
  repeat: RepeatMode = 'all';
  private loadToken = 0;

  constructor(private readonly lib: Library) {
    super();
    const a = document.createElement('audio');
    a.preload = 'auto';
    a.crossOrigin = 'anonymous'; // 跨域音频需服务端返回 CORS 头，否则 Web Audio 读到静音
    a.setAttribute('playsinline', '');
    document.body.appendChild(a);
    this.audio = a;
    a.addEventListener('ended', () => this.onEnded());
    for (const ev of ['play', 'pause', 'waiting', 'playing', 'loadedmetadata', 'durationchange']) {
      a.addEventListener(ev, () => this.emit('state'));
    }
    a.addEventListener('error', () => {
      if (!a.getAttribute('src')) return;
      this.emit('error', { message: '音频加载失败：' + (this.track?.title ?? '') });
    });
    this.setupMediaSession();
  }

  get track(): Track | undefined {
    const id = this.playlist?.tracks[this.index];
    return id ? this.lib.tracks.get(id) : undefined;
  }

  get playing(): boolean {
    return !this.audio.paused && !this.audio.ended;
  }

  get duration(): number {
    const d = this.audio.duration;
    return Number.isFinite(d) ? d : (this.track?.duration ?? 0);
  }

  /** 加载播单中的某一曲。autoplay=true 时会尝试播放。 */
  async select(playlistId: string, index: number, autoplay = true): Promise<void> {
    const pl = this.lib.playlist(playlistId);
    if (!pl) return;
    this.playlist = pl;
    this.index = Math.max(0, Math.min(index, pl.tracks.length - 1));
    const track = this.track;
    const token = ++this.loadToken;
    this.audio.pause();
    if (!track || !this.lib.isPlayable(track)) {
      this.audio.removeAttribute('src');
      this.audio.load();
      this.emit('track', { track, playlist: pl, index: this.index });
      this.emit('state');
      return;
    }
    const url = track.kind === 'test-tone' && track.testTone ? testToneUrl(track.testTone) : track.src!;
    this.audio.src = url;
    this.audio.loop = this.repeat === 'one';
    this.emit('track', { track, playlist: pl, index: this.index });
    this.updateMediaSession(track, pl);
    if (autoplay && token === this.loadToken) await this.play();
  }

  async play(): Promise<void> {
    if (!this.audio.getAttribute('src')) return;
    try {
      await this.audio.play();
    } catch (e) {
      if ((e as Error).name !== 'AbortError') this.emit('error', { message: '无法播放：' + (e as Error).message });
    }
  }

  pause(): void {
    this.audio.pause();
  }

  toggle(): void {
    if (this.playing) this.pause();
    else void this.play();
  }

  next(autoplay = true): void {
    if (!this.playlist?.tracks.length) return;
    const n = this.playlist.tracks.length;
    void this.select(this.playlist.id, (this.index + 1) % n, autoplay);
  }

  /** 播放超过 3 秒时「上一曲」先回到本曲开头 */
  prev(): void {
    if (!this.playlist?.tracks.length) return;
    if (this.audio.currentTime > 3) {
      this.seek(0);
      return;
    }
    const n = this.playlist.tracks.length;
    void this.select(this.playlist.id, (this.index - 1 + n) % n, true);
  }

  seek(t: number): void {
    if (!this.audio.getAttribute('src')) return;
    this.audio.currentTime = Math.max(0, Math.min(t, this.duration || t));
    this.emit('seek');
  }

  setRepeat(mode: RepeatMode): void {
    this.repeat = mode;
    this.audio.loop = mode === 'one';
    this.emit('state');
  }

  private onEnded(): void {
    if (this.repeat === 'one') return; // loop 属性已处理
    const last = this.index >= (this.playlist?.tracks.length ?? 0) - 1;
    if (this.repeat === 'none' && last) return;
    this.next(true);
  }

  private emit(type: string, detail?: unknown): void {
    this.dispatchEvent(new CustomEvent(type, { detail }));
  }

  /* ---------------- 系统媒体控制（锁屏 / 耳机按键） ---------------- */

  private setupMediaSession(): void {
    if (!('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    const safe = (action: MediaSessionAction, fn: MediaSessionActionHandler) => {
      try {
        ms.setActionHandler(action, fn);
      } catch {
        /* 部分浏览器不支持某些动作 */
      }
    };
    safe('play', () => void this.play());
    safe('pause', () => this.pause());
    safe('previoustrack', () => this.prev());
    safe('nexttrack', () => this.next());
    safe('seekto', (d) => d.seekTime !== undefined && this.seek(d.seekTime));
  }

  private updateMediaSession(track: Track, pl: Playlist): void {
    if (!('mediaSession' in navigator) || typeof MediaMetadata === 'undefined') return;
    navigator.mediaSession.metadata = new MediaMetadata({ title: track.title, artist: track.artist ?? '', album: `在线念佛机 · ${pl.title}` });
  }
}
